import { useEffect, useRef, useState } from 'react';
import { evidenceUrl, readForecast, type ForecastSnapshot } from '../../adapters/forecast-http';
import { QuantifiedAttentionQueue } from './AttentionEvidenceDrawer';
import { gridSelection } from './weather-selection';

export function useForecast(mode: 'LIVE' | 'DEMO' | null) {
  const [state, setState] = useState<{
    data: ForecastSnapshot | null;
    unavailable: boolean;
    mode: typeof mode;
  }>({ data: null, unavailable: false, mode: null });
  useEffect(() => {
    if (!mode) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const read = async () => {
      try {
        const data = await readForecast(mode, controller.signal);
        if (!controller.signal.aborted) setState({ data, unavailable: false, mode });
      } catch {
        if (!controller.signal.aborted) setState({ data: null, unavailable: true, mode });
      } finally {
        if (!controller.signal.aborted) timer = setTimeout(() => void read(), 10000);
      }
    };
    void read();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [mode]);
  return state.mode === mode && mode ? state : { data: null, unavailable: false };
}
export function WeatherAttention({
  data,
  unavailable,
  mode,
  areaId,
  focus,
  selectedId = '',
  onSelect,
}: {
  data: ForecastSnapshot | null;
  unavailable: boolean;
  mode: 'LIVE' | 'DEMO';
  areaId: string;
  focus: (longitude: number, latitude: number, id: string) => void;
  selectedId?: string;
  onSelect: (id: string) => void;
}) {
  const section = useRef<HTMLElement>(null);
  const forecasts = data?.forecasts.filter((f) => !areaId || f.region.regionId === areaId) ?? [];
  const evaluations = data?.evaluations.filter((e) => !areaId || e.regionId === areaId) ?? [];
  const point = forecasts[0];
  const selectedGrid = forecasts.find((f) => gridSelection(f) === selectedId);
  const hasSelectedGrid = !!selectedGrid;
  const health = data?.health.filter((h) => !areaId || h.regionId === areaId) ?? [];
  const readLabel = unavailable
    ? '预报不可用 · 不能判断风险'
    : !data
      ? '正在读取未来天气…'
      : !forecasts.length
        ? '当前区域无可用预报 · 不能判断风险'
        : `来源 ${[...new Set(health.map((h) => h.status))].join(' / ') || 'UNKNOWN'} · ${forecasts.length} 个预报时段`;
  useEffect(() => {
    if (hasSelectedGrid) section.current?.scrollIntoView({ block: 'start' });
  }, [selectedId, hasSelectedGrid]);
  return (
    <section
      ref={section}
      className="risk-empty"
      data-vs12-forecast
      aria-label="未来天气与内部关注"
    >
      <strong>{mode === 'DEMO' ? 'DEMO · 气象条件关注（演示）' : 'LIVE · NWS 未来天气预报'}</strong>
      <p>
        {mode === 'DEMO'
          ? '后台合成演练：90 秒触发、30 秒恢复。非真实事件。'
          : 'AIRPORT_WEATHER_ONLY · JFK 周边天气，不能代表森林火险。LIVE 规则未批准。'}
      </p>
      <p>预报 ≠ 已发生观测 · 官方已发布警报：未接入 · 无已验证火灾模型</p>
      {mode === 'DEMO' && (
        <QuantifiedAttentionQueue
          key={mode}
          data={data}
          unavailable={unavailable}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      )}
      <details open={hasSelectedGrid}>
        <summary>气象来源与预报原始记录 · {readLabel}</summary>
        {unavailable ? (
          <p role="status">预报/关注接口不可用，不能解释为 0 风险或安全。</p>
        ) : !data ? (
          <p>正在读取未来天气…</p>
        ) : (
          <>
            {data.health
              .filter((h) => !areaId || h.regionId === areaId)
              .map((h) => (
                <p key={h.regionId}>
                  预报来源 {h.status} {h.error} · 下次采集 {h.nextAttemptAt}
                </p>
              ))}
            {!forecasts.length && (
              <p>当前区域无可用未来时段 · INSUFFICIENT_EVIDENCE / NOT_APPLICABLE</p>
            )}
            {point && (
              <button
                type="button"
                onClick={() =>
                  focus(point.region.longitude, point.region.latitude, gridSelection(point))
                }
              >
                {mode === 'DEMO' ? '定位演示网格（合成）' : '定位预报适用网格（非站点）'}
              </button>
            )}
            {forecasts.length > 0 && (
              <details open={!!selectedGrid}>
                <summary>未来 {forecasts.length} 个时段 · GRID_FORECAST</summary>
                {forecasts.map((f) => (
                  <details key={f.id} open={f.id === selectedGrid?.id}>
                    <summary>
                      {f.validFrom} → {f.validUntil}
                    </summary>
                    <p>
                      签发 {f.issuedAt}
                      <br />
                      获取 {f.retrievedAt}
                      <br />
                      质量 {f.quality} · 网格 {f.gridIdentity}
                    </p>
                    {f.metrics.map((m) => (
                      <p key={m.name}>
                        {m.name}: {m.value === null ? '缺失（不补零）' : m.value.toFixed(1)}{' '}
                        {m.unit} · {m.quality}
                      </p>
                    ))}
                    <p>
                      提供者 {f.provider} / {f.product}
                      <br />
                      版本 {f.revision}
                      <br />
                      来源 SHA256 {f.rawSha256}
                    </p>
                    {mode === 'LIVE' && (
                      <a href={f.endpoint} target="_blank" rel="noreferrer">
                        NWS 官方预报来源
                      </a>
                    )}
                  </details>
                ))}
              </details>
            )}
            {evaluations.map((e) => (
              <details key={e.id} open={selectedGrid?.region.regionId === e.regionId}>
                <summary>
                  {e.result} · {e.approvalState}
                </summary>
                <p>{e.explanation}</p>
                <p>
                  规则 {e.ruleId} v{e.ruleVersion} · 评价 {e.evaluatedAt}
                </p>
                <p>
                  质量 {e.qualityGate} / 地理 {e.spatialGate} / 时间 {e.temporalGate}
                </p>
                <p>
                  {e.corroboration} · SAME_PROVIDER_NOT_INDEPENDENT
                  <br />
                  {e.sourceLineage.join(', ')}
                </p>
                <p>
                  trace {e.traceId}
                  <br />
                  证据 {e.evidenceDigest}
                </p>
                <a href={evidenceUrl(e.id)} target="_blank" rel="noreferrer">
                  完整证据与原输入
                </a>
              </details>
            ))}
          </>
        )}
      </details>
      <p>VS10 合成监测状态独立；天气连接成功不代表其恢复。</p>
    </section>
  );
}
